import { setDatabase } from "../state/store.js";
import { deriveDbDateFromName } from "./dbDate.js";
import { getDbDateFromExcel, normalizeDbDateString, parseTpName } from "./parseHelpers.js";
import { normalizePolValue } from "../utils/sat.js";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function normPol(v) {
  return String(v ?? "").trim().toLowerCase().slice(0, 1);
}

function normNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function normInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : "";
}

function normText(v) {
  return String(v ?? "").trim();
}

function normModulation(v) {
  return String(v ?? "").trim().toUpperCase();
}

function pickRowValue(row, keys) {
  if (!row || typeof row !== "object") return "";

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
  }

  const lcMap = new Map(
    Object.keys(row).map((key) => [String(key).toLowerCase().trim(), key])
  );

  for (const key of keys) {
    const match = lcMap.get(String(key).toLowerCase().trim());
    if (!match) continue;
    const value = row[match];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function makeTpKey({ sat_pos, frequency_MHz, pol, symbolrate_kSps, tsid, onid }) {
  const sat = normText(sat_pos);
  const f = normNum(frequency_MHz);
  const p = normPol(pol);
  const sr = normNum(symbolrate_kSps);
  const t = normInt(tsid);
  const o = normInt(onid);

  if (t !== "" || o !== "") {
    return `${sat}|${f}|${p}|${sr}|${t}|${o}`;
  }

  return `${sat}|${f}|${p}|${sr}`;
}

function resolveDbDate(db, fileName = "") {
  const derived = fileName ? deriveDbDateFromName(fileName) : "";

  const dbDate =
    normalizeDbDateString(
      db?.Date ||
      db?.dbDate ||
      db?.database_date ||
      db?.meta?.dbDate
    ) ||
    derived ||
    "Datum unbekannt";

  return {
    dbDate,
    derivedDate: derived || "",
  };
}

function inferSourceType(fileName = "", fallback = "JSON") {
  const lower = String(fileName || "").toLowerCase();
  if (lower.endsWith(".json")) return "JSON";
  if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "Excel";
  return fallback;
}

function isObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/* ------------------------------------------------------------------ */
/* JSON Detection                                                     */
/* ------------------------------------------------------------------ */

function isCompressedDb(db) {
  return isObject(db) && Array.isArray(db.Satellites);
}

function isFlatDb(db) {
  return (
    isObject(db) &&
    (Array.isArray(db.transponders) ||
      Array.isArray(db.Transponders) ||
      Array.isArray(db.tp)) &&
    (Array.isArray(db.programs) ||
      Array.isArray(db.Programme) ||
      Array.isArray(db.prg))
  );
}

function detectJsonFormat(db) {
  if (isCompressedDb(db)) return "compressed";
  if (isFlatDb(db)) return "flat";
  return "unknown";
}

/* ------------------------------------------------------------------ */
/* JSON Converters                                                    */
/* ------------------------------------------------------------------ */

function convertCompressed(db) {
  const transponders = [];
  const programs = [];

  const satellites = Array.isArray(db?.Satellites) ? db.Satellites : [];

  for (const sat of satellites) {
    const satPos =
      normText(sat?.SATPrefix) ||
      normText(sat?.SATName) ||
      "";

    const tps = Array.isArray(sat?.Transponders) ? sat.Transponders : [];

    for (const tp of tps) {
      const tpNameRaw = normText(tp?.TPName);
      const meta = parseTpName(tpNameRaw, satPos);

      const sat_pos = normText(meta.sat_pos || satPos);
      const frequency_MHz = normNum(tp?.Frequency ?? tp?.frequency_MHz);
      const pol = normPol(tp?.Pol ?? tp?.pol);
      const symbolrate_kSps = normNum(tp?.SR ?? tp?.symbolrate_kSps);

      const dvb = normText(tp?.DVB ?? tp?.dvb);
      const tsid = normInt(tp?.TSID ?? tp?.tsid);
      const onid = normInt(tp?.ONID ?? tp?.onid);

      const tp_key = makeTpKey({
        sat_pos,
        frequency_MHz,
        pol,
        symbolrate_kSps,
        tsid,
        onid,
      });

      transponders.push({
        tp_key,
        tp_name:
          tpNameRaw ||
          `[${sat_pos}-${meta.tp_no || ""}] ${meta.provider || ""}`.trim(),
        provider: meta.provider || "",
        sat_pos,
        tp_no: meta.tp_no || "",
        frequency_MHz,
        pol,
        symbolrate_kSps,
        dvb,
        modulation: normModulation(tp?.PSK ?? tp?.Modulation ?? tp?.modulation),
        fec: normText(tp?.FEC ?? tp?.fec),
        tsid,
        onid,
        tsid_onid: (tsid !== "" || onid !== "") ? `${tsid}/${onid}` : "",
      });

      const services = Array.isArray(tp?.Services) ? tp.Services : [];

      for (const s of services) {
        const encRaw = normText(s?.Encrypted ?? s?.encrypted);
        const encrypted =
          !!encRaw && encRaw.toLowerCase() !== "free" && encRaw !== "";

        programs.push({
          tp_key,
          sat_pos,
          frequency_MHz,
          pol,
          symbolrate_kSps,
          tsid,
          onid,
          provider: meta.provider || "",
          program_name: normText(s?.Name ?? s?.service),
          sid: s?.SID ?? s?.sid ?? "",
          language: normText(s?.Lang ?? s?.language),
          type: normText(s?.ServiceType ?? s?.service_type),
          resolution: normText(s?.Resolution ?? s?.resolution),
          encrypted,
        });
      }
    }
  }

  return { transponders, programs, format: "compressed" };
}

function convertFlat(db) {
  const transpondersRaw = db.transponders || db.Transponders || db.tp || [];
  const programsRaw = db.programs || db.Programme || db.prg || [];

  const transponders = transpondersRaw.map((t) => {
    const sat_pos = normText(t?.sat_pos ?? t?.Sat);
    const frequency_MHz = normNum(t?.frequency_MHz ?? t?.Frequency ?? t?.freq);
    const pol = normPol(t?.pol ?? t?.Pol);
    const symbolrate_kSps = normNum(t?.symbolrate_kSps ?? t?.SR);
    const tsid = normInt(t?.tsid ?? t?.TSID);
    const onid = normInt(t?.onid ?? t?.ONID);

    const tp_key = t?.tp_key
      ? String(t.tp_key)
      : makeTpKey({ sat_pos, frequency_MHz, pol, symbolrate_kSps, tsid, onid });

    return {
      ...t,
      tp_key,
      sat_pos,
      frequency_MHz,
      pol,
      symbolrate_kSps,
      dvb: normText(t?.dvb ?? t?.DVB),
      modulation: normModulation(t?.modulation ?? t?.Modulation ?? t?.PSK),
      fec: normText(t?.fec ?? t?.FEC),
      tsid,
      onid,
      tsid_onid:
        (tsid !== "" || onid !== "") ? `${tsid}/${onid}` : (t?.tsid_onid ?? ""),
    };
  });

  const programs = programsRaw.map((p) => {
    const sat_pos = normText(p?.sat_pos ?? p?.Sat);
    const frequency_MHz = normNum(p?.frequency_MHz ?? p?.Frequency ?? p?.freq);
    const pol = normPol(p?.pol ?? p?.Pol);
    const symbolrate_kSps = normNum(p?.symbolrate_kSps ?? p?.SR);
    const tsid = normInt(p?.tsid ?? p?.TSID);
    const onid = normInt(p?.onid ?? p?.ONID);

    const tp_key = p?.tp_key
      ? String(p.tp_key)
      : makeTpKey({ sat_pos, frequency_MHz, pol, symbolrate_kSps, tsid, onid });

    return {
      ...p,
      tp_key,
      sat_pos,
      frequency_MHz,
      pol,
      symbolrate_kSps,
      tsid,
      onid,
      provider: normText(p?.provider ?? p?.Provider),
      program_name:
        p?.program_name ??
        p?.service ??
        p?.Programm ??
        p?.Name ??
        p?.Service ??
        "",
      sid: p?.sid ?? p?.SID ?? "",
      language: normText(p?.language ?? p?.Lang),
      type: normText(p?.type ?? p?.service_type ?? p?.ServiceType),
      resolution: normText(p?.resolution ?? p?.Resolution),
      encrypted: !!p?.encrypted,
    };
  });

  return { transponders, programs, format: "flat" };
}

function normalizeJsonDb(db) {
  const format = detectJsonFormat(db);

  if (format === "compressed") return convertCompressed(db);
  if (format === "flat") return convertFlat(db);

  throw new Error("Unbekanntes JSON-Format. Erwartet wird compressed oder flat.");
}

/* ------------------------------------------------------------------ */
/* Excel Parsing                                                      */
/* ------------------------------------------------------------------ */

function requireXlsx() {
  if (typeof window === "undefined" || !window.XLSX) {
    throw new Error("XLSX-Library nicht gefunden. Bitte xlsx.bundle.js laden.");
  }
  return window.XLSX;
}

function readSheetRows(workbook, sheetName) {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];
  return window.XLSX.utils.sheet_to_json(sheet, { defval: "" });
}

function parseExcelMeta(workbook) {
  const infoRows = readSheetRows(workbook, "Info");
  const metaRows = readSheetRows(workbook, "_Meta");

  let dbDate = "";
  let sourceUrl = "";

  for (const row of infoRows) {
    if (row.key === "Database_Date") dbDate = row.value;
    if (row.key === "Source_URL") sourceUrl = row.value;
  }

  if (!dbDate && metaRows.length > 0) {
    dbDate = metaRows[0]?.db_date || "";
  }

  if (!sourceUrl && metaRows.length > 0) {
    sourceUrl = metaRows[0]?.source_url || "";
  }

  return {
    dbDate: normalizeDbDateString(dbDate) || normalizeDbDateString(getDbDateFromExcel(workbook, "")) || "",
    sourceUrl: normText(sourceUrl),
  };
}

function convertExcelRows(tpRows, prgRows) {
  const transponders = tpRows.map((row) => {
    const tpNameRaw = normText(
      pickRowValue(row, [
        "alias_raw",
        "tp_name",
        "Transponder",
        "Transpondername",
        "TPName",
        "Name",
        "name",
      ])
    );
    const satRaw = pickRowValue(row, ["sat_pos", "Sat", "SAT", "Satellit", "SATPrefix"]);
    const parsedName = parseTpName(tpNameRaw, satRaw);
    const sat_pos = normText(parsedName.sat_pos || satRaw);
    const frequency_MHz = normNum(pickRowValue(row, ["frequency_MHz", "Frequenz", "freq", "Frequency"]));
    const pol = normPol(
      normalizePolValue(pickRowValue(row, ["pol", "Pol", "Polarisation", "polarisation"]))
    );
    const symbolrate_kSps = normNum(
      pickRowValue(row, ["symbolrate_kSps", "SR", "Symbolrate", "symbolrate"])
    );
    const tsid = normInt(pickRowValue(row, ["tsid", "TSID"]));
    const onid = normInt(pickRowValue(row, ["onid", "ONID"]));

    const rawTpKey = pickRowValue(row, ["tp_key", "TP_KEY", "Tp_Key", "TP_Key", "tp key", "TP KEY"]);
    const tp_key = rawTpKey
      ? String(rawTpKey)
      : makeTpKey({ sat_pos, frequency_MHz, pol, symbolrate_kSps, tsid, onid });

    return {
      tp_key,
      tp_name: tpNameRaw || normText(pickRowValue(row, ["provider", "Provider"])),
      provider: parsedName.provider || normText(pickRowValue(row, ["provider", "Provider"])),
      sat_pos,
      tp_no: normText(parsedName.tp_no || pickRowValue(row, ["tp_no", "TP-Nr", "TPNr", "TPNo"])),
      frequency_MHz,
      pol,
      symbolrate_kSps,
      dvb: normText(pickRowValue(row, ["dvb", "DVB"])),
      modulation: normModulation(
        pickRowValue(row, ["modulation", "Modulation", "PSK", "psk"])
      ),
      fec: normText(pickRowValue(row, ["fec", "FEC"])),
      tsid,
      onid,
      tsid_onid:
        (tsid !== "" || onid !== "")
          ? `${tsid}/${onid}`
          : normText(pickRowValue(row, ["TSID/ONID", "tsid_onid"])),
    };
  });

  const programs = prgRows.map((row) => {
    const sat_pos = normText(pickRowValue(row, ["sat_pos", "Sat", "SAT", "Satellit", "SATPrefix"]));
    const frequency_MHz = normNum(pickRowValue(row, ["frequency_MHz", "Frequenz", "freq", "Frequency"]));
    const pol = normPol(
      normalizePolValue(pickRowValue(row, ["pol", "Pol", "Polarisation", "polarisation"]))
    );
    const symbolrate_kSps = normNum(
      pickRowValue(row, ["symbolrate_kSps", "SR", "Symbolrate", "symbolrate"])
    );
    const tsid = normInt(pickRowValue(row, ["tsid", "TSID"]));
    const onid = normInt(pickRowValue(row, ["onid", "ONID"]));

    const rawTpKey = pickRowValue(row, ["tp_key", "TP_KEY", "Tp_Key", "TP_Key", "tp key", "TP KEY"]);
    const tp_key = rawTpKey
      ? String(rawTpKey)
      : makeTpKey({ sat_pos, frequency_MHz, pol, symbolrate_kSps, tsid, onid });

    const encRaw = String(
      pickRowValue(row, ["encrypted", "Verschluesselt", "Encrypted"])
    ).trim().toLowerCase();
    const encrypted =
      encRaw === "true" ||
      encRaw === "1" ||
      encRaw === "yes" ||
      encRaw === "ja";

    return {
      tp_key,
      sat_pos,
      frequency_MHz,
      pol,
      symbolrate_kSps,
      tsid,
      onid,
      provider: normText(pickRowValue(row, ["provider", "Provider"])),
      program_name: normText(
        pickRowValue(row, [
          "service",
          "Service",
          "service_name",
          "program_name",
          "Programm",
          "Program",
          "Name",
          "name",
        ])
      ),
      sid: pickRowValue(row, ["sid", "SID", "ServiceID", "service_id"]) ?? "",
      language: normText(pickRowValue(row, ["language", "Sprache", "Lang", "Language", "Audio"])),
      type: normText(pickRowValue(row, ["service_type", "ServiceType", "type", "Typ"])),
      resolution: normText(pickRowValue(row, ["resolution", "Aufloesung", "Resolution"])),
      encrypted,
    };
  });

  return { transponders, programs, format: "excel-flat" };
}

async function parseExcelFile(file) {
  const XLSX = requireXlsx();
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  const sheetNames = workbook.SheetNames || [];
  const findSheet = (candidates) => {
    for (const candidate of candidates) {
      const found = sheetNames.find(
        (name) => String(name).toLowerCase() === String(candidate).toLowerCase()
      );
      if (found) return found;
    }
    return "";
  };

  const tpSheetName = findSheet(["Transponder", "tp", "Transponderliste"]) || sheetNames[0] || "";
  const prgSheetName =
    sheetNames.find((name) => String(name).toLowerCase().includes("programm")) ||
    sheetNames.find((name) => String(name).toLowerCase().includes("program")) ||
    sheetNames[1] ||
    sheetNames[0] ||
    "";

  const tpRows = readSheetRows(workbook, tpSheetName);
  const prgRows = readSheetRows(workbook, prgSheetName);

  if (!tpRows.length) {
    throw new Error("Excel enthält kein Blatt 'Transponder' oder es ist leer.");
  }

  const rows = convertExcelRows(tpRows, prgRows);
  const meta = parseExcelMeta(workbook);

  return { rows, meta };
}

/* ------------------------------------------------------------------ */
/* Store Integration                                                  */
/* ------------------------------------------------------------------ */

function applyDatabase(rows, meta) {
  setDatabase({
    transponders: rows.transponders,
    programs: rows.programs,
    sourceType: meta.sourceType,
    date: meta.dbDate,
    fileName: meta.fileName,
    derivedDate: meta.derivedDate,
  });

  return {
    transponders: rows.transponders,
    programs: rows.programs,
    dbDate: meta.dbDate,
    format: rows.format,
    fileName: meta.fileName,
    derivedDate: meta.derivedDate,
    sourceType: meta.sourceType,
  };
}

function applyJsonDatabase(db, fileName = "online.json") {
  const rows = normalizeJsonDb(db);
  const { dbDate, derivedDate } = resolveDbDate(db, fileName);

  return applyDatabase(rows, {
    dbDate,
    derivedDate,
    fileName,
    sourceType: inferSourceType(fileName, "JSON"),
  });
}

function applyExcelDatabase(parsed, fileName = "import.xlsx") {
  const derived = fileName ? deriveDbDateFromName(fileName) : "";
  const dbDate =
    parsed.meta.dbDate ||
    derived ||
    "Datum unbekannt";

  return applyDatabase(parsed.rows, {
    dbDate,
    derivedDate: derived || "",
    fileName,
    sourceType: inferSourceType(fileName, "Excel"),
  });
}

/* ------------------------------------------------------------------ */
/* Public API                                                         */
/* ------------------------------------------------------------------ */

export async function loadJsonFile(file) {
  if (!file) {
    throw new Error("Keine Datei übergeben.");
  }

  const txt = await file.text();
  let db;

  try {
    db = JSON.parse(txt);
  } catch (err) {
    throw new Error(`Ungültige JSON-Datei: ${err.message}`);
  }

  return applyJsonDatabase(db, file.name || "lokale-datei.json");
}

export function loadJsonData(data, fileName = "online.json") {
  if (!data || typeof data !== "object") {
    throw new Error("Ungültige JSON-Daten.");
  }

  return applyJsonDatabase(data, fileName);
}

export function parseJsonText(text, fileName = "text.json") {
  let db;

  try {
    db = JSON.parse(text);
  } catch (err) {
    throw new Error(`Ungültiger JSON-Text: ${err.message}`);
  }

  return applyJsonDatabase(db, fileName);
}

export async function loadExcelFile(file) {
  if (!file) {
    throw new Error("Keine Excel-Datei übergeben.");
  }

  const parsed = await parseExcelFile(file);
  return applyExcelDatabase(parsed, file.name || "import.xlsx");
}

export function detectJsonDbFormat(data) {
  return detectJsonFormat(data);
}
